import { Injectable } from '@angular/core';
import { registerLocaleData } from '@angular/common';
import { TranslocoService } from '@ngneat/transloco';
import { Preferences } from '@capacitor/preferences';
import localeEn from '@angular/common/locales/en';
import localeFr from '@angular/common/locales/fr';
import moment from 'moment';
import 'moment/locale/fr';
import { startWith, take } from 'rxjs/operators';
import { Observable } from 'rxjs';

export interface Language {
  code: string;
  name: string;
  flag: string;
  enabled?: boolean; // Control visibility in UI while keeping translation support
}

export const DEFAULT_LOCALE_CODE = 'en-US';

/** Transloco language the app boots in before a facility locale is applied. */
export const DEFAULT_LANG = 'en';

/** Preferences key for the operator's last manually-selected locale. */
export const PERSISTED_LOCALE_KEY = 'selected_language';

export function toTranslocoLang(code: string): string {
  return code?.split('-')[0] ?? code;
}

@Injectable({
  providedIn: 'root'
})
export class LanguageService {
  public readonly languages: Language[] = [
    { code: 'en', name: 'English (EN)', flag: '🇺🇸', enabled: true },
    { code: 'fr', name: 'Français (FR)', flag: '🇫🇷', enabled: true }
  ];

  constructor(private readonly translocoService: TranslocoService) {}

  /**
   * Register locale data for all supported languages
   */
  private registerAllLocales(): void {
    registerLocaleData(localeEn, 'en');
    registerLocaleData(localeFr, 'fr');
  }

  /**
   * The app always boots in English; the facility primary (or the operator's
   * persisted pick, restored on auth once the multilingual flag confirms the
   * switcher is on) is applied afterward. Never boot straight into a
   * persisted language here — a device carrying one from a build with the
   * switcher on would otherwise start in that language with the feature off
   * and no way to change it back.
   */
  initializeLanguage() {
    this.registerAllLocales();
    this.setLanguage(DEFAULT_LANG);
  }

  public setLanguage(localeCode: string, persist = false) {
    const lang = toTranslocoLang(localeCode);
    const isValidLanguage = this.languages.some(({ code }) => code === lang);
    if (!isValidLanguage) {
      console.warn('Language not found:', localeCode);
      return;
    }

    if (persist) {
      Preferences.set({ key: PERSISTED_LOCALE_KEY, value: localeCode });
    }

    this.translocoService
      .load(lang)
      .pipe(take(1))
      .subscribe(() => {
        moment.locale(lang);
        this.translocoService.setActiveLang(lang);
      });
  }

  /** Operator's last manually-selected locale code, or null if never set. */
  public async getPersistedLocaleCode(): Promise<string | null> {
    const { value } = await Preferences.get({ key: PERSISTED_LOCALE_KEY });
    return value;
  }

  public getCurrentLanguage(): string {
    return this.translocoService.getActiveLang();
  }

  public getCurrentLanguageName(): string {
    const currentLang = this.getCurrentLanguage();
    return (
      this.languages.find((lang) => lang.code === currentLang)?.name ||
      'English'
    );
  }

  public get getCurrentLocale$(): Observable<string> {
    return this.translocoService.langChanges$.pipe(
      startWith(this.getCurrentLanguage())
    );
  }
}
